"use client";

import type { HTMLAttributes, FC, Ref } from "react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { isString, mergeClasses } from "@huub-web/utils";

import type {
  ClassNameProp,
  EdgesProps,
  ElevatedProp,
  RequiredChildrenProp,
} from "../../props";
import { edgesStyle, listRowStyle, trailingEdgeStyle } from "./ListRow.css";
import { Edges } from "../Edges/Edges";
import { borders } from "../../theme/css/borders.css";
import { elevations } from "../../theme/css/elevations.css";
import { Text, type TextProps } from "../Text/Text";

export type ListRowProps = HTMLAttributes<HTMLDivElement> &
  RequiredChildrenProp &
  ClassNameProp &
  ElevatedProp &
  EdgesProps &
  RecipeVariants<typeof listRowStyle> & {
    textVariant?: TextProps["variant"];
    contentClassName?: string;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * A row in a list.
 */
export const ListRow: FC<ListRowProps> = ({
  className,
  contentClassName,
  leading,
  trailing,
  elevated = false,
  variant = "default",
  size,
  textVariant,
  children,
  ref,
  ...props
}) => (
  <div
    ref={ref}
    className={mergeClasses(
      listRowStyle({ variant, size }),
      className,
      variant === "primary" ? borders.default : undefined,
      elevated ? elevations.l : undefined,
    )}
    {...props}
  >
    <Edges
      leading={leading}
      trailing={trailing}
      trailingClassName={trailingEdgeStyle({
        variant: variant === "compact" ? "compact" : undefined,
      })}
      className={mergeClasses(edgesStyle, contentClassName)}
    >
      {isString(children) ? (
        <Text
          className={trailingEdgeStyle({})}
          ellipsis={variant !== "compact" && !trailing}
          color={variant === "compact" ? "currentColor50" : undefined}
          variant={textVariant}
        >
          {children}
        </Text>
      ) : (
        children
      )}
    </Edges>
  </div>
);
